import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { DocCallout, DocCodeBlock, DocSection } from '../docs-components';

const Webhooks: FC = () => (
  <Box>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Webhooks
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      Webhooks allow your application to receive real-time notifications when events occur on your Thermia devices, eliminating the need for constant
      polling.
    </Typography>

    <DocSection title="How It Works">
      <Typography variant="body1">
        You register a webhook by providing a publicly accessible HTTPS URL. When a subscribed event occurs, we send an HTTP <code>POST</code> request
        to that URL with a JSON payload describing the event. Your endpoint must respond with <code>2xx</code> within 10 seconds, otherwise the
        delivery is considered failed and retried.
      </Typography>
    </DocSection>

    <DocSection title="Registering a Webhook">
      <DocCodeBlock language="bash">
        {`curl -X POST https://api.thermiaonline.com/v1/webhooks \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://example.com/thermia-webhook",
    "events": ["device.status_changed", "device.alarm_triggered"],
    "secret": "your-webhook-secret"
  }'`}
      </DocCodeBlock>
    </DocSection>

    <DocSection title="Available Events">
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            textAlign: 'left',
            padding: '8px 12px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            typography: 'body2'
          },
          '& th': { fontWeight: 700, bgcolor: 'action.hover' }
        }}>
        <thead>
          <tr>
            <th>Event</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['device.status_changed', 'Fired when operating mode, temperatures or other status fields change.'],
            ['device.alarm_triggered', 'Fired when the device reports a new alarm or fault code.'],
            ['device.alarm_resolved', 'Fired when a previously active alarm is cleared.'],
            ['device.connected', 'Fired when the device comes online after being offline.'],
            ['device.disconnected', 'Fired when the device goes offline.']
          ].map(([event, desc]) => (
            <tr key={event}>
              <td>
                <code>{event}</code>
              </td>
              <td>{desc}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </DocSection>

    <DocSection title="Payload Example">
      <DocCodeBlock language="json">
        {`{
  "id": "evt_01hx3k7p2q",
  "event": "device.status_changed",
  "deviceId": "abc123",
  "timestamp": "2026-03-04T11:05:22Z",
  "data": {
    "indoorTemperature": 22.1,
    "outdoorTemperature": -4.0,
    "operatingMode": "heating",
    "isOnline": true
  }
}`}
      </DocCodeBlock>
    </DocSection>

    <DocSection title="Verifying Signatures">
      <Typography variant="body1" sx={{ mb: 1.5 }}>
        Every delivery includes an <code>X-Thermia-Signature</code> header containing an HMAC-SHA256 signature of the raw request body using your
        webhook secret. Always verify this before processing the payload:
      </Typography>
      <DocCodeBlock language="javascript">
        {`import crypto from 'crypto';

function verifySignature(body, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(body, 'utf8')
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signature)
  );
}

// In your Express handler:
app.post('/thermia-webhook', express.raw({ type: 'application/json' }), (req, res) => {
  const sig = req.headers['x-thermia-signature'];
  if (!verifySignature(req.body, sig, process.env.WEBHOOK_SECRET)) {
    return res.status(401).send('Invalid signature');
  }
  const event = JSON.parse(req.body);
  // handle event...
  res.sendStatus(200);
});`}
      </DocCodeBlock>
    </DocSection>

    <DocSection title="Retry Policy">
      <Typography variant="body1">
        If your endpoint returns a non-<code>2xx</code> status or times out, we retry the delivery up to 5 times with exponential back-off: 5s, 30s,
        5m, 30m, 2h. After all retries are exhausted the delivery is marked as failed and no further retries are attempted.
      </Typography>
    </DocSection>

    <DocCallout type="warning">Webhook endpoints must use HTTPS. Plain HTTP URLs will be rejected during registration.</DocCallout>
  </Box>
);

export default Webhooks;
